import { getSkuDetailsById } from "../../services/item/item";

const normalizeKey = (key) => String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const flattenRecord = (source, depth, bag) => {
  if (!source || typeof source !== "object" || Array.isArray(source) || depth > 4) return bag;

  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === "object") return;
    const norm = normalizeKey(key);
    if (!bag.has(norm) || bag.get(norm) === "" || bag.get(norm) == null) {
      bag.set(norm, value);
    }
  });

  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenRecord(value, depth + 1, bag);
    }
  });

  return bag;
};

export const readInventoryField = (item, ...aliases) => {
  const bag = flattenRecord(item, 0, new Map());
  for (const alias of aliases) {
    const value = bag.get(normalizeKey(alias));
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const textOf = (value) => (value == null ? "" : String(value).trim());

const isLottableEntry = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("value" in value || "label" in value);

/** Prefer { label, value }.value. A plain string is only a fallback. */
const readLottableParts = (item, index) => {
  const pad = String(index).padStart(2, "0");
  const wanted = new Set([
    normalizeKey(`lottable${pad}`),
    normalizeKey(`lottable${index}`),
  ]);
  let primitive = "";
  let structured = null;
  const seen = new Set();

  const visit = (node, depth) => {
    if (!node || typeof node !== "object" || depth > 6 || seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach((entry) => visit(entry, depth + 1));
      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (!wanted.has(normalizeKey(key)) || value == null || value === "") return;
      if (isLottableEntry(value)) {
        if (!structured) {
          structured = {
            value: textOf(value.value),
            label: textOf(value.label),
          };
        }
        return;
      }
      if ((typeof value === "string" || typeof value === "number") && !primitive) {
        primitive = textOf(value);
      }
    });

    Object.values(node).forEach((value) => {
      if (value && typeof value === "object") visit(value, depth + 1);
    });
  };

  visit(item, 0);
  if (structured) return { found: true, structured: true, ...structured };
  if (primitive) return { found: true, structured: false, value: primitive, label: "" };
  return { found: false, structured: false, value: "", label: "" };
};

export const readLottable = (item, index) => readLottableParts(item, index).value;

const skuCache = new Map();

export async function loadSkuRecords(skuIds = []) {
  const ids = [...new Set(skuIds.map((id) => String(id || "").trim()).filter(Boolean))];
  await Promise.all(ids.map(async (skuId) => {
    const key = skuId.toLowerCase();
    if (skuCache.has(key)) return;
    try {
      skuCache.set(key, await getSkuDetailsById(skuId));
    } catch (error) {
      skuCache.set(key, null);
    }
  }));
  return skuCache;
}

export function skuRecordFor(skuId) {
  return skuCache.get(String(skuId || "").trim().toLowerCase()) || null;
}

/** Lottable columns show payload `value`, never `label`. */
export function lottableAt(item, index) {
  const own = readLottableParts(item, index);
  if (own.structured || own.found) return own.value;
  const sku = skuRecordFor(item?.skuId || item?.item);
  if (!sku) return "";
  const fromSku = readLottableParts(sku, index);
  return fromSku.structured ? fromSku.value : "";
}

/** lottable04.value → Manufacturing Date, lottable05.value → Expiration Date. */
export function lottableDateAt(item, index) {
  return lottableAt(item, index);
}

/** Lottable 09(Label Text) uses payload `label`. */
export function lottableLabelAt(item, index) {
  const own = readLottableParts(item, index);
  if (own.label) return own.label;
  const sku = skuRecordFor(item?.skuId || item?.item);
  if (!sku) return "";
  return readLottableParts(sku, index).label;
}

export const readInventoryStatus = (item) => {
  const value = readInventoryField(
    item,
    "status",
    "STATUS",
    "lotStatus",
    "inventoryStatus",
    "invStatus",
    "qtyStatus"
  );
  if (value === 1 || value === "1") return "Active";
  if (value === 2 || value === "2") return "Pending";
  if (value === 3 || value === "3") return "Inactive";
  return value;
};
